/**
 * Widget Wrapper Component
 *
 * Base wrapper for all dashboard widgets providing:
 * - Header with title and controls
 * - Edit mode selection highlight
 * - Refresh functionality
 * - Loading and error states
 * - Resize overlay in edit mode
 *
 * @author BizClick WMS
 * @version 1.2.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  X,
  GripVertical,
  Move,
} from 'lucide-react';
import { WidgetInstance } from '../types';
import { getWidgetById } from '../widgetRegistry';
import { useDashboard } from '../DashboardContext';
import ResizeOverlay from './ResizeOverlay';

interface WidgetWrapperProps {
  instance: WidgetInstance;
  children: React.ReactNode;
  onRefresh?: () => void;
  isLoading?: boolean;
  error?: string | null;
}

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  instance,
  children,
  onRefresh,
  isLoading = false,
  error = null,
}) => {
  const { state, removeWidget, setSelectedWidget } = useDashboard();
  const { isEditMode, selectedWidgetId } = state;
  const widgetDef = getWidgetById(instance.widgetId);

  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [showResizeOverlay, setShowResizeOverlay] = useState(false);
  const isSelected = selectedWidgetId === instance.instanceId;

  const title = instance.settings.title || widgetDef?.name || 'Widget';
  const showHeader = instance.settings.showHeader;

  // Auto-refresh logic
  useEffect(() => {
    const interval = instance.settings.refreshInterval;
    if (interval === 0 || !onRefresh) return;

    const timer = setInterval(() => {
      onRefresh();
      setLastRefresh(new Date());
    }, interval * 1000);

    return () => clearInterval(timer);
  }, [instance.settings.refreshInterval, onRefresh]);

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
      setLastRefresh(new Date());
    }
  }, [onRefresh]);

  const handleRemove = useCallback(() => {
    removeWidget(instance.instanceId);
  }, [removeWidget, instance.instanceId]);

  const handleSelect = useCallback(() => {
    if (isEditMode) {
      setSelectedWidget(isSelected ? null : instance.instanceId);
    }
  }, [isEditMode, isSelected, setSelectedWidget, instance.instanceId]);

  const handleResizeClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowResizeOverlay(true);
  }, []);

  return (
    <>
      <div
        className={`
          relative h-full w-full flex flex-col
          bg-gray-800 rounded-lg overflow-hidden
          border-2 transition-all duration-200
          ${isEditMode ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isSelected ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-gray-700'}
          ${isEditMode && !isSelected ? 'hover:border-gray-500' : ''}
        `}
        onClick={handleSelect}
      >
        {/* Header */}
        {showHeader && (
          <div className="flex items-center justify-between px-3 py-2 bg-gray-700/50 border-b border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {isEditMode && (
                <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <h3 className="text-sm font-semibold text-white truncate">{title}</h3>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Refresh button */}
              {onRefresh && !isEditMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRefresh();
                  }}
                  className="p-1 text-gray-400 hover:text-white hover:bg-gray-600 rounded transition-colors"
                  title={`Sist oppdatert: ${lastRefresh.toLocaleTimeString('nb-NO')}`}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
              )}

              {/* Edit mode controls */}
              {isEditMode && (
                <>
                  {/* Resize button */}
                  <button
                    onClick={handleResizeClick}
                    className="p-1 text-gray-400 hover:text-blue-400 hover:bg-gray-600 rounded transition-colors"
                    title="Endre størrelse"
                  >
                    <Move className="w-3.5 h-3.5" />
                  </button>
                  {/* Remove button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove();
                    }}
                    className="p-1 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded transition-colors"
                    title="Fjern widget"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-3 overflow-auto min-h-0">
          {error ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-red-400">
                <p className="text-sm">Feil ved lasting</p>
                <p className="text-xs text-gray-500 mt-1">{error}</p>
                {onRefresh && (
                  <button
                    onClick={handleRefresh}
                    className="mt-2 px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
                  >
                    Prøv igjen
                  </button>
                )}
              </div>
            </div>
          ) : (
            children
          )}
        </div>

        {/* Size indicator and resize button in edit mode */}
        {isEditMode && (
          <button
            onClick={handleResizeClick}
            className="absolute bottom-1 right-1 px-2 py-0.5 text-[10px] text-gray-400 bg-gray-700/90 hover:bg-blue-600 hover:text-white rounded transition-colors cursor-pointer"
            title="Klikk for å endre størrelse"
          >
            {instance.size.width} x {instance.size.height}
          </button>
        )}

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-gray-800/50 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        )}
      </div>

      {/* Resize Overlay Modal */}
      {showResizeOverlay && (
        <ResizeOverlay
          instance={instance}
          onClose={() => setShowResizeOverlay(false)}
        />
      )}
    </>
  );
};

export default WidgetWrapper;
